import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, MessageSquare } from 'lucide-react';
import { firstTrainingDate } from '../history/heatmapData';
import { mondayOf } from '../logic/schedule';
import { FONT_MONO } from '../theme';
import { Card } from '../ui';
import { CardTitle, extraColors } from './common';
import { fmtSets, shortDate } from './statsData';
import { buildWeekReview, reviewWeekStart } from './weekReview';

const DAY_INITIALS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const MAX_BACK = 25; // about six months of weeks to page through

function NavButton({ t, label, disabled, onClick, children }) {
  return (
    <button type="button" aria-label={label} title={label} disabled={disabled} onClick={onClick} style={{
      width: 40, height: 40, borderRadius: 10, flexShrink: 0,
      background: 'transparent', border: `1px solid ${t.cardBorder}`, color: t.textMuted,
      cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.35 : 1,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>{children}</button>
  );
}

function Metric({ t, label, value, sub, color }) {
  return (
    <div style={{ background: t.surfaceAlt, borderRadius: 10, padding: '10px 12px', minWidth: 0 }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: t.textFaint }}>{label}</div>
      <div style={{ fontSize: 18, fontFamily: FONT_MONO, fontWeight: 700, color: color || t.text, marginTop: 3, lineHeight: 1.15 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</div>}
    </div>
  );
}

/* One labelled line of the review; the label column keeps the lines aligned. */
function Line({ t, label, color, children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '64px minmax(0, 1fr)', gap: 8, fontSize: 12, lineHeight: 1.45, padding: '6px 0', borderTop: `1px solid ${t.divider}` }}>
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: color || t.textFaint, paddingTop: 2 }}>{label}</span>
      <span style={{ color: t.text, minWidth: 0, overflowWrap: 'anywhere' }}>{children}</span>
    </div>
  );
}

function fmtKg(v) {
  const r = Math.round(v * 10) / 10;
  return `${r > 0 ? '+' : r < 0 ? '−' : '±'}${Math.abs(r).toFixed(1)} kg`;
}

/* Train / Protein / Creatine / Weigh-in, Monday to Sunday. A missed planned
 * session gets the amber outline the History calendar uses. */
function HabitStrip({ t, c, habits }) {
  const cols = '68px repeat(7, minmax(0, 1fr)) 36px';
  const dot = (habit, cell, i) => {
    const base = { width: 14, height: 14, borderRadius: '50%', boxSizing: 'border-box', display: 'inline-block' };
    let style;
    if (cell === 'hit') style = { ...base, background: t.accent };
    else if (cell === 'miss') style = { ...base, border: `1.5px solid ${habit.id === 'train' ? c.amber : t.cardBorder}` };
    else style = { ...base, width: 4, height: 4, background: t.textFaint };
    const what = cell === 'hit' ? 'yes' : cell === 'miss' ? (habit.id === 'train' ? 'missed' : 'no') : (habit.id === 'train' ? 'rest' : 'not logged');
    return (
      <span key={i} title={`${habit.label} ${DAY_INITIALS[i]}: ${what}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 22 }}>
        <span style={style} />
      </span>
    );
  };
  return (
    <div role="table" aria-label="Habits this week" style={{ marginTop: 6 }}>
      <div role="row" style={{ display: 'grid', gridTemplateColumns: cols, alignItems: 'center' }}>
        <span />
        {DAY_INITIALS.map((d, i) => (
          <span key={i} style={{ fontSize: 9, fontWeight: 700, color: t.textFaint, textAlign: 'center' }}>{d}</span>
        ))}
        <span />
      </div>
      {habits.map(h => (
        <div key={h.id} role="row" style={{ display: 'grid', gridTemplateColumns: cols, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: t.textMuted, whiteSpace: 'nowrap' }}>{h.label}</span>
          {h.cells.map((cell, i) => dot(h, cell, i))}
          <span style={{ fontSize: 12, fontFamily: FONT_MONO, fontWeight: 600, color: t.text, textAlign: 'right' }}>
            {h.of ? `${h.hit}/${h.of}` : '–'}
          </span>
        </div>
      ))}
    </div>
  );
}

/* The last completed Mon-Sun week at a glance, computed from the logs with
 * the shared logic (the numbers Claude's weekly review starts from). Nothing
 * is stored. The chevrons page back through earlier weeks. */
export function WeekReviewCard({ t, isDark, state, today, events }) {
  const [back, setBack] = useState(0);
  const c = extraColors(isDark);
  const weekStart = reviewWeekStart(today, back);
  const review = useMemo(
    () => (weekStart ? buildWeekReview(state, weekStart, { today, events }) : null),
    [state, weekStart, today, events]
  );
  const firstDate = useMemo(() => firstTrainingDate(state.logs), [state.logs]);
  // Nothing to review before the first session ever.
  if (!review || (!firstDate && !review.hasData)) return null;
  const firstMonday = firstDate ? mondayOf(firstDate) : null;
  const olderDisabled = back >= MAX_BACK || !firstMonday || weekStart <= firstMonday;

  const { sessions, prs, movers, muscles, nutrition, weight } = review;
  const missedText = sessions.missed.map(m => `${m.planDay || 'Session'} (${shortDate(m.date)})`).join(' · ');
  const up = movers.up.slice(0, 3);
  const down = movers.down.slice(0, 3);

  return (
    <Card t={t} padding={16}>
      <CardTitle t={t}
        sub={`${shortDate(review.weekStart)} – ${shortDate(review.weekEnd)}${back === 0 ? ' · last week' : ''}`}
        right={(
          <div style={{ display: 'flex', gap: 6 }}>
            <NavButton t={t} label="Earlier week" disabled={olderDisabled} onClick={() => setBack(b => b + 1)}>
              <ChevronLeft size={18} />
            </NavButton>
            <NavButton t={t} label="Later week" disabled={back === 0} onClick={() => setBack(b => Math.max(0, b - 1))}>
              <ChevronRight size={18} />
            </NavButton>
          </div>
        )}>
        Week {review.week} review
      </CardTitle>

      <div style={{ fontSize: 16, fontWeight: 700, color: t.text, lineHeight: 1.3, marginBottom: 12 }}>
        {review.headline}
      </div>

      <div className="fit-quad" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8, marginBottom: 8 }}>
        <Metric t={t} label="Sessions" value={`${sessions.done}/${sessions.planned}`}
          color={sessions.missed.length ? c.amber : sessions.planned && sessions.done >= sessions.planned ? t.accent : undefined}
          sub={sessions.extra ? `${sessions.extra} on a rest day` : sessions.missed.length ? `${sessions.missed.length} missed` : 'planned'} />
        <Metric t={t} label="PRs" value={prs.length} color={prs.length ? c.gold : undefined}
          sub={prs.length ? `${new Set(prs.map(e => e.exercise)).size} lift${new Set(prs.map(e => e.exercise)).size === 1 ? '' : 's'}` : 'none this week'} />
        <Metric t={t} label="Avg kcal"
          value={nutrition && nutrition.avgKcal !== null ? Math.round(nutrition.avgKcal).toLocaleString() : '—'}
          sub={nutrition ? `${nutrition.loggedDays} complete day${nutrition.loggedDays === 1 ? '' : 's'}` : 'no food logged'} />
        <Metric t={t} label="Weight"
          value={weight.change !== null ? fmtKg(weight.change) : weight.avg !== null ? weight.avg.toFixed(1) : '—'}
          sub={weight.avg !== null ? `avg ${weight.avg.toFixed(1)} kg` : 'no weigh-ins'} />
      </div>

      {sessions.missed.length > 0 && <Line t={t} label="Missed" color={c.amber}>{missedText}</Line>}

      {prs.length > 0 && (
        <Line t={t} label="PRs" color={c.gold}>
          {prs.slice(-4).reverse().map(e => `${e.exercise} ${e.set.weight} × ${e.set.reps}`).join(' · ')}
          {prs.length > 4 && <span style={{ color: t.textMuted }}> · +{prs.length - 4} more</span>}
        </Line>
      )}

      {(up.length > 0 || down.length > 0) && (
        <Line t={t} label="Lifts">
          {up.length > 0 && (
            <span style={{ display: 'block' }}>
              <span style={{ color: t.accent, fontWeight: 600 }}>Up: </span>
              {up.map(r => `${r.name}${r.trend.slopePctPerWeek > 0 ? ` +${r.trend.slopePctPerWeek.toFixed(1)}%/wk` : ''}`).join(' · ')}
            </span>
          )}
          {down.length > 0 && (
            <span style={{ display: 'block' }}>
              <span style={{ color: c.amber, fontWeight: 600 }}>Watch: </span>
              {down.map(r => `${r.name} (${r.chip})`).join(' · ')}
            </span>
          )}
        </Line>
      )}

      {muscles.total > 0 && (
        <Line t={t} label="Sets">
          {muscles.top.slice(0, 4).map(m => `${m.muscle} ${fmtSets(m.sets)}`).join(' · ')}
          {muscles.verdict && muscles.verdict.muscle && (
            <span style={{ display: 'block', color: t.textMuted }}>{muscles.verdict.text}</span>
          )}
        </Line>
      )}

      {nutrition && (
        <Line t={t} label="Food">
          {nutrition.avgProtein !== null && `${Math.round(nutrition.avgProtein)} g protein a day`}
          {nutrition.proteinPerKg !== null && ` (${nutrition.proteinPerKg} g/kg)`}
          {nutrition.kcalOnTargetDays !== null && nutrition.loggedDays > 0 && (
            <span style={{ display: 'block', color: t.textMuted }}>
              kcal within 10% on {nutrition.kcalOnTargetDays} of {nutrition.loggedDays} complete days
            </span>
          )}
        </Line>
      )}

      <div style={{ borderTop: `1px solid ${t.divider}`, paddingTop: 8, marginTop: 2 }}>
        <HabitStrip t={t} c={c} habits={review.habits} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 12, color: t.textMuted }}>
        <MessageSquare size={14} style={{ flexShrink: 0 }} />
        <span>Ask Claude: <span style={{ color: t.text, fontWeight: 600 }}>"do my weekly review"</span></span>
      </div>
    </Card>
  );
}
